import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ornament } from "../components/Ornament";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function StoryWeekComplete() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [profileId, setProfileId] = useState(null);

  // Beta interest opt-in
  const [betaState, setBetaState] = useState("idle"); // idle | saving | already | done | error
  const [betaError, setBetaError] = useState("");

  // PDF download state — on demand, nothing stored
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/story-week", { replace: true }); return; }
    (async () => {
      try {
        const s = await api.get("/story_week/mine");
        setSession(s.data);
      } catch (e) { /* no session — send to welcome */ }
      try {
        const p = await api.get("/profiles");
        const claimed = (p.data || []).find((x) => x.source === "story_week");
        if (claimed) setProfileId(claimed.profile_id);
      } catch (e) { /* ignore */ }
      // If they've already opted into beta interest (or full application), show it.
      try {
        const m = await api.get("/beta/mine");
        if (m.data?.applied) setBetaState("already");
      } catch (e) { /* ignore */ }
    })();
  }, [user, loading, navigate]);

  const openMyStories = async () => {
    setError(""); setBusy(true);
    try {
      if (profileId) {
        navigate(`/app/stories/${profileId}`);
        return;
      }
      const teller = session?.storyteller_name || user.name || "Story Keeper";
      const rel = session?.storyteller_relation || "Self";
      const res = await api.post("/story_week/claim", {
        storyteller_name: teller,
        storyteller_relation: rel,
      });
      const pid = res.data?.profile_id;
      if (pid) navigate(`/app/stories/${pid}`);
      else navigate("/dashboard");
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't open your stories just now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const registerBetaInterest = async () => {
    setBetaError("");
    setBetaState("saving");
    try {
      const res = await api.post("/beta/interest");
      if (res.data?.already_registered) setBetaState("already");
      else setBetaState("done");
    } catch (e) {
      setBetaError(e?.response?.data?.detail || "Couldn't save that just now. Please try again in a moment.");
      setBetaState("error");
    }
  };

  const downloadPdf = async () => {
    setPdfError(""); setPdfBusy(true);
    // Open a tab synchronously so the browser does not treat it as a popup.
    const win = window.open("", "_blank");
    try {
      const res = await api.get("/story_week/export.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (win && !win.closed) {
        win.location.href = url;
      } else {
        window.location.href = url;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    } catch (e) {
      if (win && !win.closed) win.close();
      // Blob responses hide JSON errors — try to read them if present
      let msg = "Couldn't build your Living Book just now. Please try again in a moment.";
      try {
        const txt = await e?.response?.data?.text?.();
        if (txt) { const j = JSON.parse(txt); if (j?.detail) msg = j.detail; }
      } catch (_) { /* ignore */ }
      setPdfError(msg);
    } finally {
      setPdfBusy(false);
    }
  };

  const daysDone = session ? Object.keys(session.days || {}).length : 0;
  const skipped = session?.skipped_days?.length || 0;

  return (
    <div className="min-h-screen paper" data-testid="story-week-complete">
      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24 text-center">
        <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.35em]">STORY WEEK — SEVEN CONVERSATIONS</p>
        <h1 className="mt-8 text-4xl sm:text-6xl" style={{ fontFamily: "Newsreader, serif", fontWeight: 300, lineHeight: 1.1 }}>
          Seven days. Seven answers.
          <br/><span style={{ fontStyle: "italic", color: "#B8973A" }}>Well done.</span>
        </h1>
        <Ornament className="my-10" />
        <p className="max-w-2xl mx-auto text-[#1B2A4A]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "17px", lineHeight: 1.75 }}>
          Your responses are safe in your account — beside the exact question you answered, with your original voice recordings and any photographs you added.
        </p>
        <p className="mt-4 text-[#8C8278]" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "15px" }} data-testid="sw-complete-summary">
          {daysDone} answered{skipped > 0 ? `, ${skipped} skipped` : ""}. Every response stays editable.
        </p>

        {error && <p className="mt-4 text-red-700 text-sm" role="alert">{error}</p>}
        {pdfError && <p className="mt-4 text-red-700 text-sm" role="alert" data-testid="sw-complete-pdf-error">{pdfError}</p>}

        <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
          <button
            type="button"
            onClick={downloadPdf}
            disabled={pdfBusy}
            className="btn-outline-navy"
            style={{ minHeight: 52, padding: "14px 28px" }}
            data-testid="sw-complete-download-pdf"
          >
            {pdfBusy ? "Preparing your Living Book…" : "See your Living Book so far"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/founding-beta")}
            className="btn-gold"
            style={{ minHeight: 52, padding: "14px 28px" }}
            data-testid="sw-complete-continue-full"
          >
            Continue and complete your full Living Book
          </button>
          <button
            type="button"
            onClick={() => navigate("/my-living-book")}
            className="btn-outline-navy"
            style={{ minHeight: 52, padding: "14px 28px" }}
            data-testid="sw-complete-view-mlb"
          >
            View My Living Book
          </button>
          <button
            type="button"
            onClick={openMyStories}
            disabled={busy}
            className="btn-outline-navy"
            style={{ minHeight: 52, padding: "14px 28px" }}
            data-testid="sw-complete-open-btn"
          >
            {busy ? "Opening…" : "Return to My Seven Stories"}
          </button>
        </div>

        {/* Optional beta-testing invitation — warm, one tap, uses existing account */}
        <aside
          className="mt-20 mx-auto text-left"
          style={{
            maxWidth: 560,
            background: "#FAF7F2",
            border: "1px solid #E8D5A3",
            borderRadius: 4,
            padding: "28px 28px 24px",
            boxShadow: "0 1px 0 rgba(184,151,58,0.08)",
          }}
          data-testid="sw-complete-beta-card"
        >
          <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.3em]" style={{ marginBottom: 10 }}>
            AN OPTIONAL INVITATION
          </p>
          <h2
            className="text-2xl sm:text-3xl"
            style={{ fontFamily: "Newsreader, serif", fontWeight: 400, color: "#111D33", lineHeight: 1.2 }}
          >
            The full Living Book app is coming soon.
          </h2>
          <p
            className="mt-3 text-[#1B2A4A]"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "16px", lineHeight: 1.65 }}
          >
            We&rsquo;re gathering a small circle of early testers to shape it with us. There&rsquo;s nothing else to fill in — we&rsquo;ll use the name and email already on your account.
          </p>

          {betaState === "already" && (
            <p
              className="mt-5 text-[#5C5548] italic"
              style={{ fontFamily: "Newsreader, serif", fontSize: "1.05rem" }}
              data-testid="sw-complete-beta-already"
            >
              Beautiful — you&rsquo;re already on the beta-testers list. We&rsquo;ll write to you personally when it&rsquo;s ready.
            </p>
          )}

          {betaState === "done" && (
            <p
              className="mt-5 text-[#5C5548] italic"
              style={{ fontFamily: "Newsreader, serif", fontSize: "1.05rem" }}
              data-testid="sw-complete-beta-done"
            >
              Thank you. We&rsquo;ve added you to the beta-testers list and we&rsquo;ll be in touch when a spot opens.
            </p>
          )}

          {(betaState === "idle" || betaState === "saving" || betaState === "error") && (
            <>
              <button
                type="button"
                onClick={registerBetaInterest}
                disabled={betaState === "saving"}
                className="btn-gold mt-5"
                style={{ minHeight: 48, padding: "12px 24px", fontSize: "13px", letterSpacing: "0.15em" }}
                data-testid="sw-complete-beta-optin-btn"
              >
                {betaState === "saving" ? "Saving…" : "I'd love to beta test The Living Book"}
              </button>
              <p
                className="mt-3 text-[#8C8278]"
                style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "14px" }}
              >
                No new account. No marketing list. Just a note that you&rsquo;re interested.
              </p>
              {betaState === "error" && betaError && (
                <p className="mt-3 text-red-700 text-sm" role="alert" data-testid="sw-complete-beta-error">
                  {betaError}
                </p>
              )}
            </>
          )}
        </aside>

        <p className="mt-16 italic text-[#8C8278]" style={{ fontFamily: "Newsreader, serif", fontSize: "1.1rem" }}>
          The conversation is the gift. The book is simply where it comes to rest.
        </p>
      </main>
    </div>
  );
}
